import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Calendar, Clock, Link as LinkIcon, MapPin, Save } from 'lucide-react'
import type { ChangeEvent } from 'react'
import type { Event } from '@/lib/types'
import type { components } from '@/lib/client'
import {
  Button,
  Card,
  Combobox,
  FormTip,
  ImageInput,
  Input,
} from '@/components/ui'
import { fieldLabelStyle } from '@/components/ui/Input'
import MarkdownEditor from '@/components/LazyMarkdownEditor'
import EventCard from '@/components/EventCard'
import { EventStatus } from '@/lib/constants'
import { $client } from '@/lib/fetch'
import { searchAddress } from '@/lib/geocode'

type CreateEvent = components['schemas']['CreateEvent']
/** Event poster — Instagram-style portrait crop (600 wide x 750 tall). */
const EVENT_IMAGE_ASPECT = 600 / 750

/** ISO string → value for <input type="datetime-local"> (local time). */
function toLocalInput(iso: string | undefined): string {
  if (!iso) return ''
  const d = new Date(iso)
  if (isNaN(d.getTime())) return ''
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours(),
  )}:${pad(d.getMinutes())}`
}

/** datetime-local value → absolute ISO string. */
function fromLocalInput(value: string): string {
  return new Date(value).toISOString()
}

const STATUS_OPTIONS = [
  { value: EventStatus.NotVerified, label: 'En attente' },
  { value: EventStatus.Verified, label: 'Vérifié' },
  { value: EventStatus.SoftDelete, label: 'Supprimé' },
]

export default function EventForm({
  label,
  event,
  isAdmin,
  onSubmit,
}: {
  label: string
  event: Event
  isAdmin: boolean
  /** Returns a map of field → error message, or void on success. */
  onSubmit: (data: CreateEvent) => Promise<Record<string, string> | void>
}) {
  const { data } = useQuery({
    queryKey: ['event-form-data'],
    queryFn: async () => {
      const [t, e] = await Promise.all([
        $client.GET('/api/events/types'),
        $client.GET('/api/entities'),
      ])
      return {
        types: t.data?.types ?? [],
        entities: e.data?.entities ?? [],
      }
    },
  })

  const [form, setForm] = useState({
    title: event.title,
    address: event.location.address,
    description: event.description,
    type: event.type.type,
    status: event.status,
    external_link: event.external_link ?? '',
    entity_id: event.entity?.id ? String(event.entity.id) : '',
    start: toLocalInput(event.start_date),
    end: toLocalInput(event.end_date),
  })
  const [newImage, setNewImage] = useState<{
    base64: string
    type: string
  } | null>(null)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [loading, setLoading] = useState(false)

  const set =
    (k: keyof typeof form) =>
    (
      e: ChangeEvent<
        HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
      >,
    ) =>
      setForm((f) => ({ ...f, [k]: e.target.value }))

  const types = data?.types ?? []
  const entities = data?.entities ?? []

  const imagePreview = newImage
    ? `data:${newImage.type};base64,${newImage.base64}`
    : null

  const submit = async () => {
    setLoading(true)
    const payload: CreateEvent = {
      title: form.title,
      description: form.description,
      address: form.address,
      type: form.type,
      status: form.status,
      start_date: fromLocalInput(form.start),
      end_date: form.end ? fromLocalInput(form.end) : undefined,
      external_link: form.external_link || undefined,
      entity_id: form.entity_id ? parseInt(form.entity_id) : undefined,
      new_image: newImage ?? undefined,
    }
    const result = await onSubmit(payload).finally(() => setLoading(false))
    setErrors(result || {})
  }

  // Live preview view-model (re-uses EventCard).
  const previewType = types.find((t) => t.type === form.type) ?? event.type
  const previewEvent: Event = {
    ...event,
    title: form.title || "Titre de l'événement",
    description: form.description,
    location: { ...event.location, address: form.address || 'Lieu' },
    start_date: form.start ? fromLocalInput(form.start) : event.start_date,
    type: previewType,
    image_uri:
      imagePreview ?? event.image_uri ?? (previewType.image_uri || undefined),
    image_blurhash: imagePreview ? undefined : event.image_blurhash,
  }

  const err = (k: string) =>
    errors[k] ? (
      <span style={{ fontSize: 13, color: 'var(--danger)' }}>{errors[k]}</span>
    ) : null

  return (
    <div
      style={{
        maxWidth: 'var(--container-max)',
        margin: '0 auto',
        padding: 'var(--space-8) var(--gutter) var(--space-20)',
      }}
    >
      <div className="oumma-overline" style={{ marginBottom: 10 }}>
        Proposer un événement
      </div>
      <h1 style={{ fontSize: 'var(--text-h1)', margin: '0 0 8px' }}>{label}</h1>
      <p
        style={{
          fontSize: 18,
          color: 'var(--ink-2)',
          maxWidth: 580,
          marginBottom: 32,
        }}
      >
        Dites à la communauté ce que vous organisez. L'adresse sera localisée
        automatiquement.
      </p>

      <div
        className="oumma-propose-grid"
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0,1.5fr) minmax(300px,1fr)',
          gap: 40,
          alignItems: 'start',
        }}
      >
        <Card padding={28}>
          <form
            onSubmit={(e) => {
              e.preventDefault()
              submit()
            }}
            style={{ display: 'flex', flexDirection: 'column', gap: 20 }}
          >
            <div>
              <Input
                label="Nom de l'événement"
                placeholder="ex. Iftar communautaire à la mosquée"
                value={form.title}
                onChange={set('title')}
                required
              />
              {err('title')}
            </div>

            <div>
              <ImageInput
                label="Affiche ou photo"
                aspect={EVENT_IMAGE_ASPECT}
                value={event.image_uri}
                onChange={setNewImage}
                onError={(msg) => setErrors((p) => ({ ...p, new_image: msg }))}
              />
              {err('new_image')}
            </div>

            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: 16,
              }}
              className="oumma-set-grid"
            >
              <div>
                <Combobox
                  label="Type d'événement"
                  options={types.map((t) => ({
                    value: t.type,
                    label: `${t.emoji} ${t.type}`,
                  }))}
                  value={form.type}
                  onChange={(v) => setForm((f) => ({ ...f, type: v }))}
                />
                {err('type')}
              </div>
              {isAdmin && (
                <Combobox
                  label="Statut"
                  options={STATUS_OPTIONS}
                  value={form.status}
                  onChange={(v) => setForm((f) => ({ ...f, status: v }))}
                />
              )}
            </div>

            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: 16,
              }}
              className="oumma-set-grid"
            >
              <div>
                <Input
                  label="Début"
                  type="datetime-local"
                  iconLeft={<Calendar size={18} />}
                  value={form.start}
                  onChange={set('start')}
                  required
                />
                {err('start_date')}
              </div>
              <Input
                label="Fin (facultatif)"
                type="datetime-local"
                iconLeft={<Clock size={18} />}
                value={form.end}
                onChange={set('end')}
              />
            </div>

            <div>
              <Combobox
                label="Adresse"
                placeholder="ex. Grande Mosquée de Paris, Paris"
                icon={<MapPin size={18} />}
                freeText
                loadOptions={searchAddress}
                value={form.address}
                onChange={(v) => setForm((f) => ({ ...f, address: v }))}
              />
              {err('address')}
            </div>

            <div>
              <label style={fieldLabelStyle}>Description</label>
              <MarkdownEditor
                value={form.description}
                onChange={(v) =>
                  set('description')({ target: { value: v } } as any)
                }
                placeholder="À quoi s'attendre ? À qui s'adresse l'événement ?"
              />
              {err('description')}
            </div>

            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: 16,
              }}
              className="oumma-set-grid"
            >
              <Combobox
                label="Organisateur (facultatif)"
                options={[
                  { value: '', label: 'Aucun' },
                  ...entities.map((e) => ({
                    value: String(e.id),
                    label: e.name,
                  })),
                ]}
                value={form.entity_id}
                onChange={(v) => setForm((f) => ({ ...f, entity_id: v }))}
              />
              <Input
                label="Lien externe"
                iconLeft={<LinkIcon size={18} />}
                placeholder="https://…"
                value={form.external_link}
                onChange={set('external_link')}
              />
            </div>

            <div
              style={{
                display: 'flex',
                gap: 12,
                justifyContent: 'flex-end',
                flexWrap: 'wrap',
              }}
            >
              <Button
                variant="primary"
                type="submit"
                loading={loading}
                iconLeft={<Save size={18} />}
              >
                Enregistrer
              </Button>
            </div>
          </form>
        </Card>

        <div
          className="oumma-propose-side"
          style={{ position: 'sticky', top: 76 }}
        >
          <div
            style={{
              fontSize: 12,
              fontWeight: 700,
              letterSpacing: '0.16em',
              textTransform: 'uppercase',
              color: 'var(--ink-3)',
              marginBottom: 12,
            }}
          >
            Aperçu en direct
          </div>
          <EventCard event={previewEvent} preview />
          <FormTip>
            Voici comment votre événement apparaîtra. La couleur dépend du type.
            Tout est relu avant publication.
          </FormTip>
        </div>
      </div>
    </div>
  )
}
